import { config } from '../../config/Config.js';

/**
 * The app's connection / session / version state, in one place outside React, so the axios layer, Firebase and the
 * version poll can all report into it and one status bar and one dialog read it. Every state here has a way out the
 * user can see (see docs in AppStatusBar / SessionExpiredDialog) - none of them is a dead end.
 *
 *   offline           the browser says there is no network
 *   serverUnreachable requests reach no server (network error / 502); a probe clears it when the server answers
 *   sessionExpired    the session can no longer be refreshed - the sign-in dialog is up (sessionExpiry.js)
 *   newVersion        a different build is deployed than the one this tab runs (versionCheck.js)
 */

/** Fired when the app is back in touch with the server (online again, the server answers). Loaders that gave up re-run. */
export const RECONNECTED_EVENT = 'gp-app-reconnected';

const PROBE_INTERVAL_MS = 10000;
const PROBE_TIMEOUT_MS = 5000;

let state = {
    offline: typeof navigator !== 'undefined' && navigator.onLine === false,
    serverUnreachable: false,
    sessionExpired: false,
    newVersion: null,
};
const listeners = new Set();

export const getAppStatus = () => state;

export const subscribeAppStatus = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const setAppStatus = (patch) => {
    const next = { ...state, ...patch };
    if (Object.keys(next).every((k) => next[k] === state[k])) return;
    state = next;
    listeners.forEach((l) => l(state));
};

const announceReconnected = () => {
    try {
        window.dispatchEvent(new Event(RECONNECTED_EVENT));
    } catch (_) { /* non-browser - ignore */ }
};

let probeTimer = null;

/**
 * One request to the API server: any answer below 500 means it is there. When the API is on another origin the answer
 * is opaque (no CORS on a bare HEAD), so getting one at all has to do.
 */
export const probeServer = async () => {
    if (!config.serverBaseUrl) return true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    try {
        const sameOrigin = new URL(config.serverBaseUrl, window.location.href).origin === window.location.origin;
        const res = await fetch(`${config.serverBaseUrl}/`, {
            method: 'HEAD', cache: 'no-store', mode: sameOrigin ? 'same-origin' : 'no-cors', signal: controller.signal,
        });
        return res.type === 'opaque' || res.status < 500;
    } catch {
        return false;
    } finally {
        clearTimeout(timer);
    }
};

const scheduleProbe = () => {
    clearTimeout(probeTimer);
    probeTimer = setTimeout(async () => {
        if (!state.serverUnreachable) return;
        if (await probeServer()) markServerReachable();
        else scheduleProbe();
    }, PROBE_INTERVAL_MS);
};

/** A request reached no server. Shows "Can't reach GeoPlay" and keeps probing until it answers. */
export const markServerUnreachable = () => {
    if (state.serverUnreachable) return;
    setAppStatus({ serverUnreachable: true });
    scheduleProbe();
};

/** The server answered again: clear the bar and let loaders that gave up try again. */
export const markServerReachable = () => {
    clearTimeout(probeTimer);
    if (!state.serverUnreachable) return;
    setAppStatus({ serverUnreachable: false });
    announceReconnected();
};

/** "Retry now" on the bar: probe at once instead of waiting for the next tick. */
export const retryServerNow = async () => {
    if (await probeServer()) markServerReachable();
    else scheduleProbe();
};

if (typeof window !== 'undefined') {
    window.addEventListener('offline', () => setAppStatus({ offline: true }));
    window.addEventListener('online', () => {
        setAppStatus({ offline: false });
        announceReconnected();
    });
}
